/**
 * Form Structure Module
 *
 * This module contains utilities for extracting and managing form structure from canvas:
 * - Extracting form structure from canvas objects
 * - Finding objects by path
 * - Sorting fields by key order
 * - Building field objects from canvas objects
 * - Processing game group attachments
 * - Getting field labels and updating form fields
 */

import {
  GRAPHICS_ALLOWED_MOVEMENT_PAYLOAD_TYPES,
  GRAPHICS_NOT_ALLOWED_PAYLOAD_TYPES,
  GRAPHICS_TEMPLATE_KEY_ORDER,
  GRAPHICS_TEMPLATE_PLAYER_NAME_NAMETYPE_KEYS,
} from "constants/ThumbnailEditor";

import { parseStyledHandlebarText } from "./text-operations";

/**
 * Extracts player name information from a field
 *
 * @param {Object} field - Field object to analyze
 * @returns {Object|null} Object with playerNumber and nameType, or null if not a player name field
 */
const extractPlayerNameInfo = (field) => {
  const key = field?.templatePayload?.key || "";
  const nameType = field?.templatePayload?.nameType;

  // Check if this is a player name field (e.g., "player_1_name", "player_2_name")
  const playerNameMatch = key.match(/^player_(\d+)_name$/);

  if (playerNameMatch && nameType) {
    return {
      playerNumber: parseInt(playerNameMatch[1], 10),
      nameType: nameType,
    };
  }

  return null;
};

/**
 * Sort fields by children presence first, then by templatePayload.key order
 * Special handling for player name fields: sort by player number, then by nameType
 *
 * @param {Array} fields - Array of field objects to sort
 * @returns {Array} Sorted fields array
 */
const sortFieldsByKeyOrder = (fields) => {
  return fields.sort((a, b) => {
    const aHasChildren = !!a.children;
    const bHasChildren = !!b.children;

    // 1️⃣ Leaf nodes first
    if (aHasChildren !== bHasChildren) {
      return aHasChildren ? 1 : -1;
    }

    // 2️⃣ Special handling for player name fields
    const aPlayerInfo = extractPlayerNameInfo(a);
    const bPlayerInfo = extractPlayerNameInfo(b);

    if (aPlayerInfo && bPlayerInfo) {
      // Both are player name fields
      // First, sort by player number (1, 2, 3, 4)
      if (aPlayerInfo.playerNumber !== bPlayerInfo.playerNumber) {
        return aPlayerInfo.playerNumber - bPlayerInfo.playerNumber;
      }

      // If same player number, sort by nameType order
      const aNameTypeIndex = GRAPHICS_TEMPLATE_PLAYER_NAME_NAMETYPE_KEYS.indexOf(aPlayerInfo.nameType);
      const bNameTypeIndex = GRAPHICS_TEMPLATE_PLAYER_NAME_NAMETYPE_KEYS.indexOf(bPlayerInfo.nameType);

      const aNameTypeOrder = aNameTypeIndex === -1 ? Infinity : aNameTypeIndex;
      const bNameTypeOrder = bNameTypeIndex === -1 ? Infinity : bNameTypeIndex;

      return aNameTypeOrder - bNameTypeOrder;
    }

    // 3️⃣ Then by templatePayload.key order
    const aKey = a.templatePayload?.key || "";
    const bKey = b.templatePayload?.key || "";

    const aIndex = GRAPHICS_TEMPLATE_KEY_ORDER.indexOf(aKey);
    const bIndex = GRAPHICS_TEMPLATE_KEY_ORDER.indexOf(bKey);

    const aOrder = aIndex === -1 ? Infinity : aIndex;
    const bOrder = bIndex === -1 ? Infinity : bIndex;

    return aOrder - bOrder;
  });
};

/**
 * Build field object from canvas object with all necessary properties
 *
 * @param {Object} obj - Canvas object
 * @param {string} path - Object path
 * @param {Object} typeCount - Count of each type
 * @param {Object} typeIndexTracker - Index tracker for types
 * @returns {Object} Field object
 */
const buildFieldFromObject = (obj, path, typeCount, typeIndexTracker) => {
  const field = {
    path,
    type: obj.type,
    templatePayload: obj.templatePayload,
    groupValue: obj?.groupValue,
  };

  // ➕ Add index only if multiple of same templatePayload.type exist
  const objType = obj.templatePayload.type;
  if (typeCount[objType] > 1 || obj.templatePayload.key === "game") {
    typeIndexTracker[objType] = (typeIndexTracker[objType] || 0) + 1;
    field.indexForType = typeIndexTracker[objType];
  }

  // Type-specific data
  if (["textbox", "text", "i-text"].includes(obj.type)) {
    field.text = obj.text || "";
  } else if (obj.type === "image") {
    field.src = obj._element?.currentSrc || obj._element?.src || obj.src || "";
  } else if (obj.type === "group") {
    const groupChildren = obj._objects || obj.objects;
    if (groupChildren) {
      field.children = extractFormStructure(groupChildren, `${path}.objects`);
    }
  }

  return field;
};

/**
 * Process gameGroup property for movable objects and attach them to matching game groups
 * If corresponding game is not present, add game attachments to root fields
 *
 * @param {Array} formFields - Extracted form fields
 * @returns {Array} Fields with gameGroup objects attached to their respective game groups or added to root
 */
const processGameGroupAttachments = (formFields) => {
  const gameGroupedFields = [];
  const fieldsWithGameGroup = [];

  // Separate fields with gameGroup from others
  formFields.forEach((field) => {
    const gameGroup = field.templatePayload?.gameGroup;
    const isMovableType =
      field.templatePayload?.type && GRAPHICS_ALLOWED_MOVEMENT_PAYLOAD_TYPES.includes(field.templatePayload.type);

    if (gameGroup && isMovableType) {
      fieldsWithGameGroup.push(field);
    } else {
      gameGroupedFields.push(field);
    }
  });

  // Group fields by their gameGroup tag
  const groupedByGame = {};
  fieldsWithGameGroup.forEach((field) => {
    const gameGroup = field.templatePayload.gameGroup;
    if (!groupedByGame[gameGroup]) {
      groupedByGame[gameGroup] = [];
    }
    groupedByGame[gameGroup].push(field);
  });

  // Find existing game groups (e.g., "game_1", "game_2")
  const existingGameTags = new Set();
  gameGroupedFields.forEach((field) => {
    const isGameGroup = field.type === "group" && field.templatePayload?.key === "game";
    if (isGameGroup && field.indexForType) {
      const gameTag = `game_${field.indexForType}`;
      existingGameTags.add(gameTag);
    }
  });

  // Process each game group tag
  Object.entries(groupedByGame).forEach(([gameTag, taggedFields]) => {
    if (existingGameTags.has(gameTag)) {
      // Game exists - attach fields as virtual children
      gameGroupedFields.forEach((field) => {
        const isGameGroup = field.type === "group" && field.templatePayload?.key === "game";

        if (isGameGroup && field.indexForType) {
          const currentGameTag = `game_${field.indexForType}`;

          if (currentGameTag === gameTag && taggedFields.length > 0) {
            // Initialize children array if it doesn't exist
            if (!field.children) {
              field.children = [];
            }

            // Add tagged fields as virtual children at the end (sorted by key order)
            const mergedChildren = [...field.children, ...taggedFields];
            field.children = sortFieldsByKeyOrder(mergedChildren);
          }
        }
      });
    } else {
      // Game doesn't exist - add fields to root level
      if (taggedFields.length > 0) {
        gameGroupedFields.push(...taggedFields);
      }
    }
  });

  return gameGroupedFields;
};

/**
 * Extracts form structure from canvas objects
 * Recursively processes objects and their children to build a hierarchical form structure
 *
 * @param {Array} objects - Canvas objects array
 * @param {string} parentPath - Parent path for nested objects (default: "objects")
 * @returns {Array} Extracted form fields with hierarchy
 */
export const extractFormStructure = (objects = [], parentPath = "objects") => {
  const formFields = [];

  // Count occurrences of each templatePayload.type at this depth
  const typeCount = {};
  objects.forEach((obj) => {
    const type = obj?.templatePayload?.type;
    if (type) typeCount[type] = (typeCount[type] || 0) + 1;
  });

  // Maintain counters for each type (for sequential numbering)
  const typeIndexTracker = {};

  // Extract all fields from canvas objects
  objects.forEach((obj, index) => {
    const currentPath = `${parentPath}[${index}]`;

    // Skip non-templatePayload or disallowed types
    if (!obj.templatePayload || GRAPHICS_NOT_ALLOWED_PAYLOAD_TYPES.includes(obj?.templatePayload?.type)) {
      return;
    }

    const field = buildFieldFromObject(obj, currentPath, typeCount, typeIndexTracker);
    formFields.push(field);
  });

  // 🎮 Process gameGroup attachments (only at root level)
  const fieldsToSort = parentPath === "objects" ? processGameGroupAttachments(formFields) : formFields;

  // 🔹 Sort and return fields
  return sortFieldsByKeyOrder(fieldsToSort);
};

/**
 * Finds a canvas object by its path
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {string} path - Object path (e.g., "objects[0]" or "objects[1].objects[2]")
 * @returns {Object|null} Canvas object or null
 */
export const findObjectByPath = (canvas, path) => {
  if (!canvas || !path) return null;

  // Parse path to get indices
  const parts = path.split(".").map((p) => {
    const match = p.match(/objects\[(\d+)\]/);
    return match ? parseInt(match[1]) : null;
  });

  const validParts = parts.filter((n) => n !== null);
  if (validParts.length === 0) return null;

  let objects = canvas.getObjects();
  let obj = objects[validParts[0]];

  // Navigate through nested objects
  for (let i = 1; i < validParts.length; i++) {
    if (obj && obj.type === "group" && obj._objects) {
      obj = obj._objects[validParts[i]];
    } else {
      return null;
    }
  }

  return obj || null;
};

const toTitle = (text) =>
  text
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Gets a human-readable label for a form field
 * Converts template payload key to title case and adds index if applicable
 *
 * @param {Object} templatePayload - Template payload object
 * @param {number} indexForType - Index for fields of the same type
 * @returns {string} Field label
 */
export const getFieldLabel = (templatePayload, indexForType) => {
  const key = templatePayload?.key || "field";

  if (key === "custom_text" && templatePayload?.value) {
    const { templatePayloadRanges } = parseStyledHandlebarText(templatePayload?.value);

    let finalText = "";
    templatePayloadRanges.forEach((range) => {
      const rangeKey = range.templatePayload?.key;
      const rangeNameType = range.templatePayload?.nameType;
      const rangeValue = range.templatePayload?.value || "";

      if (rangeKey === "free_text") {
        finalText += rangeValue;
      } else {
        finalText += toTitle(rangeKey);

        if (rangeNameType) {
          finalText += ` (${toTitle(rangeNameType)})`;
        }
      }
    });

    return finalText;
  }

  // Default label handling
  let label = key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  if (indexForType) {
    label = `${label} #${indexForType}`;
  }

  if (templatePayload?.nameType) {
    const name = templatePayload?.nameType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    label = `${label} | ${name}`;
  }

  return label;
};

/**
 * Updates a specific form field's value by path and returns new form fields
 * Recursively searches through fields and their children to find and update the target field
 *
 * @param {Array} formFields - Current form fields array
 * @param {string} path - Path to the field to update
 * @param {*} value - New value for the field
 * @returns {Array} Updated form fields array
 */
export const getNewFormFieldsChildren = (formFields, path, value) => {
  return formFields?.map((field) => {
    if (field.path === path) {
      return { ...field, text: value };
    }
    if (field.type === "group" && field.children) {
      return { ...field, children: getNewFormFieldsChildren(field.children, path, value) };
    }
    return field;
  });
};
